/**
 * ThemeService - Manages light/dark theme for the SDK
 *
 * Supports three modes:
 * - 'light': Always use light theme
 * - 'dark': Always use dark theme
 * - 'system': Follow OS/browser preference (default)
 *
 * Theme is applied via data-theme attribute on document.documentElement
 * and a 'dark' class for Tailwind CSS dark mode support.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeServiceConfig {
  /** Initial theme preference */
  preference?: ThemePreference;
  /** Storage key for persisting preference */
  storageKey?: string;
  /** Enable debug logging */
  debug?: boolean;
}

const DEFAULT_STORAGE_KEY = 'appbouncer_theme';

/**
 * ThemeService manages application theme (light/dark mode).
 *
 * Usage:
 * ```typescript
 * const theme = new ThemeService({ preference: 'system' });
 * theme.apply(); // Applies theme to document
 *
 * // Listen for changes
 * theme.onChange((resolvedTheme) => {
 *   console.log('Theme changed to:', resolvedTheme);
 * });
 * ```
 */
export class ThemeService {
  private preference: ThemePreference;
  private resolvedTheme: ResolvedTheme;
  private storageKey: string;
  private debug: boolean;
  private mediaQuery: MediaQueryList | null = null;
  private listeners: Set<(theme: ResolvedTheme) => void> = new Set();
  private boundMediaHandler: (e: MediaQueryListEvent) => void;

  constructor(config: ThemeServiceConfig = {}) {
    this.storageKey = config.storageKey || DEFAULT_STORAGE_KEY;
    this.debug = config.debug || false;

    // Load preference from config, storage, or default to 'system'
    this.preference = config.preference || this.loadPreference() || 'system';
    this.resolvedTheme = this.resolveTheme(this.preference);

    // Bind the media query handler
    this.boundMediaHandler = this.handleSystemThemeChange.bind(this);

    this.log('ThemeService initialized:', {
      preference: this.preference,
      resolved: this.resolvedTheme,
    });
  }

  /**
   * Apply the current theme to the document.
   * Sets data-theme attribute and 'dark' class for Tailwind.
   */
  apply(): void {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    // Set data-theme attribute for custom CSS
    root.setAttribute('data-theme', this.resolvedTheme);

    // Toggle 'dark' class for Tailwind CSS dark mode
    if (this.resolvedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    this.log('Theme applied:', this.resolvedTheme);
  }

  /**
   * Set the theme preference and apply it.
   */
  setPreference(preference: ThemePreference): void {
    this.preference = preference;
    this.savePreference(preference);

    const oldResolved = this.resolvedTheme;
    this.resolvedTheme = this.resolveTheme(preference);

    this.apply();

    // Set up or tear down system theme listener
    if (preference === 'system') {
      this.startSystemThemeListener();
    } else {
      this.stopSystemThemeListener();
    }

    // Notify listeners if theme changed
    if (oldResolved !== this.resolvedTheme) {
      this.notifyListeners();
    }
  }

  /**
   * Get the current preference ('light', 'dark', or 'system').
   */
  getPreference(): ThemePreference {
    return this.preference;
  }

  /**
   * Get the resolved theme ('light' or 'dark').
   * If preference is 'system', this reflects the OS preference.
   */
  getResolvedTheme(): ResolvedTheme {
    return this.resolvedTheme;
  }

  /**
   * Check if dark mode is currently active.
   */
  isDark(): boolean {
    return this.resolvedTheme === 'dark';
  }

  /**
   * Toggle between light and dark themes.
   * If currently 'system', switches to the opposite of resolved theme.
   */
  toggle(): void {
    const newTheme: ThemePreference = this.resolvedTheme === 'dark' ? 'light' : 'dark';
    this.setPreference(newTheme);
  }

  /**
   * Subscribe to theme changes.
   * @returns Unsubscribe function
   */
  onChange(callback: (theme: ResolvedTheme) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Initialize the service: apply theme and start system listener if needed.
   */
  initialize(): void {
    this.apply();

    if (this.preference === 'system') {
      this.startSystemThemeListener();
    }
  }

  /**
   * Clean up resources (remove listeners).
   */
  dispose(): void {
    this.stopSystemThemeListener();
    this.listeners.clear();
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private resolveTheme(preference: ThemePreference): ResolvedTheme {
    if (preference === 'light' || preference === 'dark') {
      return preference;
    }

    // 'system' - detect from OS
    return this.detectSystemTheme();
  }

  private detectSystemTheme(): ResolvedTheme {
    if (typeof window === 'undefined') return 'light';

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }

  private startSystemThemeListener(): void {
    if (typeof window === 'undefined') return;

    // Clean up any existing listener
    this.stopSystemThemeListener();

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQuery.addEventListener('change', this.boundMediaHandler);

    this.log('Started system theme listener');
  }

  private stopSystemThemeListener(): void {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.boundMediaHandler);
      this.mediaQuery = null;
      this.log('Stopped system theme listener');
    }
  }

  private handleSystemThemeChange(event: MediaQueryListEvent): void {
    if (this.preference !== 'system') return;

    const oldResolved = this.resolvedTheme;
    this.resolvedTheme = event.matches ? 'dark' : 'light';

    this.log('System theme changed:', this.resolvedTheme);

    this.apply();

    if (oldResolved !== this.resolvedTheme) {
      this.notifyListeners();
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        callback(this.resolvedTheme);
      } catch (error) {
        console.error('[ThemeService] Listener error:', error);
      }
    });
  }

  private loadPreference(): ThemePreference | null {
    if (typeof localStorage === 'undefined') return null;

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      // localStorage not available
    }
    return null;
  }

  private savePreference(preference: ThemePreference): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(this.storageKey, preference);
    } catch {
      // localStorage not available
    }
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[ThemeService]', ...args);
    }
  }
}

// ============================================================
// Singleton Instance
// ============================================================

let themeServiceInstance: ThemeService | null = null;

/**
 * Get or create the global ThemeService instance.
 * Use this for simple cases where you want a shared theme service.
 */
export function getThemeService(config?: ThemeServiceConfig): ThemeService {
  if (!themeServiceInstance) {
    themeServiceInstance = new ThemeService(config);
  }
  return themeServiceInstance;
}

/**
 * Reset the global theme service instance.
 * Useful for testing or reinitializing with different config.
 */
export function resetThemeService(): void {
  if (themeServiceInstance) {
    themeServiceInstance.dispose();
    themeServiceInstance = null;
  }
}
